<script lang="ts">
	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import { get } from 'svelte/store';

	import type { Device } from 'src/types/Device';
	import type { PVSetupTypes } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';

	import { devices, home_data } from 'src/stores';

	import PvSetupTypeSelect from 'src/components/Consumption/PVSetupEnergy/PVSetupTypeSelect.svelte';
	import SolarMeterPicker from './shared/SolarMeterPicker.svelte';

	import {
		Alert,
		ButtonV2,
		Modal,
		Text,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
		toast,
	} from '@Shelly/shelly-design-system';

	import { getPVSetupDevices } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';
	import { save_home } from 'src/helpers/home_helpers';
	import { MAIN_ENERGY_METER_KEY } from './shared/main-energy-meter-helpers';
	import {
		getActiveSolarSetupType,
		getSolarMeterRoleHint,
		planSolarSetupApplianceTypes,
		type PvConfigurations,
	} from './shared/solar-setup-helpers';
	import {
		applyApplianceTypeSyncChanges,
		getSuggestedSolarGridMeterId,
	} from './shared/solar-setup-persist';

	export let show = false;

	const dispatch = createEventDispatcher<{
		close: void;
		save: { setupType: PVSetupTypes; gridDeviceIds: string[] };
	}>();

	const wizardTitles = [
		{ titleKey: 'solar_setup_step_mode', defaultTitle: 'Mode' },
		{ titleKey: 'solar_setup_step_grid', defaultTitle: 'Grid meters' },
		{ titleKey: 'solar_setup_step_solar', defaultTitle: 'Solar meters' },
	];

	let setupType: PVSetupTypes = 'Parallel';
	let lastHydratedType: PVSetupTypes | null = null;
	let gridDeviceIds: string[] = [];
	let solarDeviceIds: string[] = [];
	let activeIndex = 0;
	let saving = false;
	let wasShown = false;

	$: mainEnergyMeterId = ($home_data?.[MAIN_ENERGY_METER_KEY] as string) || null;
	$: gridHint = getSolarMeterRoleHint(setupType, 'grid');
	$: solarHint = getSolarMeterRoleHint(setupType, 'solar');
	$: canGoToSolarStep = gridDeviceIds.length > 0;
	$: canSave = gridDeviceIds.length > 0 && solarDeviceIds.length > 0;

	const showSaveError = (): void => {
		toast.open({
			type: 'danger',
			title: $format('error_global', { default: 'Something went wrong!' }),
		});
	};

	const hydrateForType = (type: PVSetupTypes): void => {
		const saved = getPVSetupDevices(type);
		gridDeviceIds = saved.gridDevices.map((device) => device.id);
		solarDeviceIds = saved.solarDevices.map((device) => device.id);
		if (gridDeviceIds.length === 0) {
			const suggestedId = getSuggestedSolarGridMeterId(
				get(home_data),
				type,
				get(devices) as Record<string, Device>,
			);
			if (suggestedId) gridDeviceIds = [suggestedId];
		}
		lastHydratedType = type;
	};

	const resetAndHydrate = (): void => {
		setupType = getActiveSolarSetupType(get(home_data));
		activeIndex = 0;
		lastHydratedType = null;
		hydrateForType(setupType);
	};

	$: if (show && !wasShown) {
		wasShown = true;
		resetAndHydrate();
	} else if (!show && wasShown) {
		wasShown = false;
	}

	$: if (show && setupType !== lastHydratedType) {
		hydrateForType(setupType);
	}

	const handleClose = (): void => {
		if (saving) return;
		dispatch('close');
	};

	const handleBack = (): void => {
		if (activeIndex > 0) activeIndex -= 1;
	};

	const handleNextOrSave = async (): Promise<void> => {
		if (activeIndex < wizardTitles.length - 1) {
			if (activeIndex === 1 && !canGoToSolarStep) return;
			activeIndex += 1;
			return;
		}

		if (!canSave || saving) return;
		saving = true;

		const previous = getPVSetupDevices(setupType);
		const typeRes = await save_home('net_metering_type', setupType);
		if (!typeRes?.isok) {
			saving = false;
			showSaveError();
			return;
		}

		const configurations = {
			...((get(home_data)?.pv_configurations || {}) as PvConfigurations),
		};
		configurations[setupType] = {
			gridDevices: [...gridDeviceIds],
			solarDevices: [...solarDeviceIds],
		};
		const configRes = await save_home('pv_configurations', configurations);
		if (!configRes?.isok) {
			saving = false;
			showSaveError();
			return;
		}

		await applyApplianceTypeSyncChanges(
			planSolarSetupApplianceTypes({
				previousGridIds: previous.gridDevices.map((device) => device.id),
				previousSolarIds: previous.solarDevices.map((device) => device.id),
				nextGridIds: gridDeviceIds,
				nextSolarIds: solarDeviceIds,
				mainEnergyMeterId,
			}),
		);

		saving = false;
		dispatch('save', { setupType, gridDeviceIds });
	};
</script>

<Modal
	showModal={show}
	title={$format('label_solar_setup', { default: 'Solar setup' })}
	backdropClose={!saving}
	removeScrollPadding
	on:close={handleClose}
>
	<Wizard slot="toolbar">
		{#each wizardTitles as item, i (item.titleKey)}
			<WizardItem
				title={$format(item.titleKey, { default: item.defaultTitle })}
				active={activeIndex === i}
				disabled={i === 2 && !canGoToSolarStep}
				indicatorColor="color-2"
				on:click={() => {
					if (i === 2 && !canGoToSolarStep) return;
					activeIndex = i;
				}}
			/>
		{/each}
	</Wizard>

	<div slot="content">
		<WizardContainer activeStepIndex={activeIndex} adaptiveHeight>
			<WizardContent active={activeIndex === 0} class="pb-s">
				<Alert
					class="mb-m"
					heading={$format('solar_setup_mode_intro_title', {
						default: 'Choose how solar is wired',
					})}
					message={$format('solar_setup_mode_intro', {
						default:
							'Each mode uses grid and solar meters differently. Pick the layout that matches this home, then designate the meters on the next steps.',
					})}
				/>
				<PvSetupTypeSelect bind:pvSetupType={setupType} />
			</WizardContent>
			<WizardContent active={activeIndex === 1} class="pb-s">
				<Text styleType="body-medium" class="mb-xs">
					{$format('solar_setup_step_grid', { default: 'Grid meters' })}
				</Text>
				<Alert class="mb-m" message={$format(gridHint.key, { default: gridHint.default })} />
				<SolarMeterPicker
					idPrefix="solar-setup-grid"
					{setupType}
					role="grid"
					{mainEnergyMeterId}
					bind:selectedDeviceIds={gridDeviceIds}
					excludedDeviceIds={solarDeviceIds}
				/>
			</WizardContent>
			<WizardContent active={activeIndex === 2} class="pb-s">
				<Text styleType="body-medium" class="mb-xs">
					{$format('solar_setup_step_solar', { default: 'Solar meters' })}
				</Text>
				<Alert class="mb-m" message={$format(solarHint.key, { default: solarHint.default })} />
				<SolarMeterPicker
					idPrefix="solar-setup-solar"
					{setupType}
					role="solar"
					{mainEnergyMeterId}
					bind:selectedDeviceIds={solarDeviceIds}
					excludedDeviceIds={gridDeviceIds}
				/>
			</WizardContent>
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end gap-s">
		<ButtonV2
			variant="secondary"
			aria-label={$format('button_cancel', { default: 'Cancel' })}
			disabled={saving}
			on:click={handleClose}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		{#if activeIndex > 0}
			<ButtonV2
				variant="secondary"
				aria-label={$format('button_back', { default: 'Back' })}
				disabled={saving}
				on:click={handleBack}
			>
				{$format('button_back', { default: 'Back' })}
			</ButtonV2>
		{/if}
		<ButtonV2
			aria-label={activeIndex === wizardTitles.length - 1
				? $format('button_save', { default: 'Save' })
				: $format('button_next', { default: 'Next' })}
			disabled={(activeIndex === 1 && !canGoToSolarStep) ||
				(activeIndex === 2 && !canSave) ||
				saving}
			loading={saving}
			on:click={handleNextOrSave}
		>
			{activeIndex === wizardTitles.length - 1
				? $format('button_save', { default: 'Save' })
				: $format('button_next', { default: 'Next' })}
		</ButtonV2>
	</div>
</Modal>
